import React, { useEffect, useState } from 'react';
import AdminTable from 'common/AdminTable';
import { getRequest, patchRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';
import { FaEye, FaTimes, FaUndo } from 'react-icons/fa';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const exportToExcel = (data) => {
  const headers = ['S.No', 'Name', 'Mobile', 'Email', 'Location', 'Subject', 'Status', 'Date'];
  const rows = data.map((row, i) => [
    i + 1,
    row.name || '',
    row.mobile || '',
    row.email || '',
    row.location || '',
    row.subject || '',
    row.is_active ? 'Open' : 'Closed',
    formatDate(row.createdAt),
  ]);

  let html = '<html><head><meta charset="UTF-8"><title>Expert Calling Export</title></head><body>';
  html += '<table border="1" cellpadding="5" cellspacing="0" style="border-collapse:collapse;font-family:Arial;font-size:13px">';
  html += '<thead><tr style="background:#4F81BD;color:#fff">';
  headers.forEach((h) => { html += `<th>${h}</th>`; });
  html += '</tr></thead><tbody>';
  rows.forEach((row) => {
    html += '<tr>';
    row.forEach((cell) => { html += `<td>${cell}</td>`; });
    html += '</tr>';
  });
  html += '</tbody></table></body></html>';

  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const matchFilter = (item, mode, dateStr, monthStr, yearStr) => {
  if (mode === 'all') return true;
  const d = new Date(item.createdAt);
  if (isNaN(d.getTime())) return false;
  const itemYear = d.getFullYear();
  const itemMonth = d.getMonth() + 1;
  const itemDay = d.getDate();

  if (mode === 'date') {
    if (!dateStr) return true;
    const [y, m, day] = dateStr.split('-').map(Number);
    return itemYear === y && itemMonth === m && itemDay === day;
  }
  if (mode === 'month') {
    if (!monthStr) return true;
    const [y, m] = monthStr.split('-').map(Number);
    return itemYear === y && itemMonth === m;
  }
  if (mode === 'year') {
    if (!yearStr) return true;
    return itemYear === Number(yearStr);
  }
  return true;
};

const ExpertCallingList = () => {
  const [data, setData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterMode, setFilterMode] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [viewEnquiry, setViewEnquiry] = useState(null);
  const limit = 10;

  const years = [];
  for (let y = 2020; y <= new Date().getFullYear() + 1; y++) years.push(y);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const getExpertData = async (pageNum = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(pageNum));
      params.set('limit', String(limit));
      params.set('include_inactive', 'true');

      const res = await getRequest(`expertCalling?${params.toString()}`, getAuthHeaders());
      if (res.data.success) {
        const items = Array.isArray(res.data.data) ? res.data.data : [];
        setData(items);
        setAllData(items);
        setTotalPages(res.data.totalPages || 1);
        setTotalItems(res.data.total || 0);
        setPage(res.data.page || pageNum);
      } else {
        toast.error(res.data.message || 'Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getExpertData(1);
  }, []);

  useEffect(() => {
    const filtered = allData.filter((item) =>
      matchFilter(item, filterMode, selectedDate, selectedMonth, selectedYear)
    );
    setFilteredData(filtered);
  }, [allData, filterMode, selectedDate, selectedMonth, selectedYear]);

  const applyFilter = (mode, date, month, year) => {
    setFilterMode(mode);
    setSelectedDate(date);
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  const displayData = filterMode === 'all' ? data : filteredData;

  const refreshCurrentPage = () => getExpertData(page);

  const handleStatusToggle = async (item) => {
    const newStatus = !item.is_active;
    setLoading(true);
    try {
      const payload = new URLSearchParams();
      payload.append('is_active', String(newStatus));
      const res = await patchRequest(`expertCalling/${item.id}`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('userToken') || sessionStorage.getItem('userToken')}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      if (res.data.success) {
        toast.success(`Enquiry marked as ${newStatus ? 'Open' : 'Closed'}`);
        getExpertData(page);
      } else {
        toast.error(res.data.message || 'Failed to update status');
      }
    } catch (error) {
      toast.error(error?.message || 'Error updating status');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) getExpertData(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) getExpertData(page + 1);
  };

  const tableHeaders = [
    { Header: 'S.No', accessor: 'sno', Cell: ({ row }) => row.index + 1 + (page - 1) * limit },
    { Header: 'Name', accessor: 'name' },
    { Header: 'Mobile', accessor: 'mobile' },
    { Header: 'Email', accessor: 'email' },
    { Header: 'Location', accessor: 'location' },
    { Header: 'Subject', accessor: 'subject' },
    {
      Header: 'Status',
      accessor: 'is_active',
      Cell: ({ row }) => (
        <span className={`badge ${row.original.is_active ? 'badge-success' : 'badge-secondary'}`}>
          {row.original.is_active ? 'Open' : 'Closed'}
        </span>
      ),
    },
    {
      Header: 'Date',
      accessor: 'createdAt',
      Cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => {
        const item = row.original;
        return (
          <div className="d-flex flex-wrap" style={{ gap: 6 }}>
            <button
              type="button"
              className="btn btn-sm btn-info"
              onClick={() => setViewEnquiry(item)}
              title="View Details"
            >
              <FaEye />
            </button>
            <button
              type="button"
              className={`btn btn-sm ${item.is_active ? 'btn-secondary' : 'btn-success'}`}
              onClick={() => handleStatusToggle(item)}
              disabled={loading}
              title={item.is_active ? 'Close' : 'Re-Open'}
            >
              {item.is_active ? <FaTimes /> : <FaUndo />}
            </button>
          </div>
        );
      },
    },
  ];

  if (loading && data.length === 0) return <div className="text-center mt-5">Loading...</div>;

  return (
    <div className="container-fluid">
      <div className="mainheadig mx-4 d-flex align-items-end justify-content-between ">
        <div>
          <h4 className="font-weight-bold mb-1">Expert Calling Enquiries</h4>
          {/* <p className="text-muted small mb-0">View and manage all expert consultation enquiries.</p> */}
        </div>
        <button className="btn btn-outline-secondary" onClick={refreshCurrentPage} disabled={loading} style={{ width: '45px', padding: '0', justifyContent: 'center' }}>
          <i className="fa fa-refresh" aria-hidden="true"></i>
        </button>
      </div>

      <div className="mx-4 my-3 p-3 d-flex align-items-center flex-wrap gap-2" style={{ background: '#f8f9fa', borderRadius: 8 }}>
        <div className="btn-group btn-group-sm mr-3" role="group">
          <button className={`btn ${filterMode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => applyFilter('all', '', '', '')}>All</button>
          <button className={`btn ${filterMode === 'date' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => applyFilter('date', '', '', '')}>Date</button>
          <button className={`btn ${filterMode === 'month' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => applyFilter('month', '', '', '')}>Month</button>
          <button className={`btn ${filterMode === 'year' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => applyFilter('year', '', '', '')}>Year</button>
        </div>

        {filterMode === 'date' && (
          <input type="date" className="form-control" style={{ maxWidth: 200 }} value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
        )}
        {filterMode === 'month' && (
          <input type="month" className="form-control" style={{ maxWidth: 200 }} value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
        )}
        {filterMode === 'year' && (
          <select className="form-control" style={{ width: 120 }} value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            <option value="">Select Year</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}

        <button className="btn btn-success btn-sm ml-auto" onClick={() => exportToExcel(displayData)}>
          <i className="fa fa-file-excel-o mr-1" aria-hidden="true"></i> Export
        </button>
      </div>

      {
        displayData.length > 0 ? (
          <>
            <AdminTable
              tableHeaders={tableHeaders}
              tableData={displayData}
            />
            <div className="d-flex justify-content-between align-items-center mt-4 mx-4">
              <div>Showing {displayData.length} of {totalItems} entries</div>
              <div>
                <button className="btn btn-secondary mx-1" onClick={handlePrevPage} disabled={page === 1}>
                  Previous
                </button>
                <span className="mx-2">Page {page} of {totalPages}</span>
                <button className="btn btn-secondary mx-1" onClick={handleNextPage} disabled={page === totalPages}>
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <p className="container-fluid bg-danger w-100 p-2 mx-4 text-center text-white">No data found</p>
        )
      }

      {/* View Enquiry Modal */}
      {viewEnquiry && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setViewEnquiry(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Escape' && setViewEnquiry(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold">Enquiry Details</h5>
                <button type="button" className="close" onClick={() => setViewEnquiry(null)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <table className="table table-bordered table-sm mb-0">
                  <tbody>
                    <tr><th className="w-25">Name</th><td>{viewEnquiry.name || '—'}</td></tr>
                    <tr><th>Mobile</th><td>{viewEnquiry.mobile || '—'}</td></tr>
                    <tr><th>Email</th><td>{viewEnquiry.email || '—'}</td></tr>
                    <tr><th>Location</th><td>{viewEnquiry.location || '—'}</td></tr>
                    <tr><th>Subject</th><td>{viewEnquiry.subject || '—'}</td></tr>
                    <tr>
                      <th>Status</th>
                      <td>
                        <span className={`badge ${viewEnquiry.is_active ? 'badge-success' : 'badge-secondary'}`}>
                          {viewEnquiry.is_active ? 'Open' : 'Closed'}
                        </span>
                      </td>
                    </tr>
                    <tr><th>Created At</th><td>{formatDateTime(viewEnquiry.createdAt)}</td></tr>
                    <tr><th>Updated At</th><td>{formatDateTime(viewEnquiry.updatedAt)}</td></tr>
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setViewEnquiry(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpertCallingList;
